import {
  armTagsApiRef,
  type TagWriteAccess,
} from '@internal/plugin-azure-resources-common-react';
import { useAsync } from 'react-use';

import { useApi } from '@backstage/core-plugin-api';

import type { ResourceGroupRow } from './buildResourceGroupRows';

export type TagWriteAccessByCandidate = Record<string, TagWriteAccess>;

/**
 * Ceiling on individual resource-group permission checks. Only reached when the
 * user holds no subscription-wide tag-write role, in which case the remaining
 * candidates stay `unknown` — editable, with the write failing loudly — rather
 * than making the modal wait on hundreds of round trips.
 */
const MAX_RESOURCE_GROUP_CHECKS = 150;

/**
 * Works out where the signed-in user may actually write tags, so rows they
 * cannot change are never editable.
 *
 * Permissions inherit downwards, so one check per subscription answers for all
 * of its resource groups; only candidates in subscriptions that do not grant
 * tag-write are checked individually, which is where resource-group-scoped role
 * assignments show up.
 */
export const useTagWriteAccess = (
  rows: ResourceGroupRow[] | undefined,
): { access: TagWriteAccessByCandidate } => {
  const armTagsApi = useApi(armTagsApiRef);

  const { value } = useAsync(async () => {
    const candidates = (rows ?? []).flatMap(row => row.candidates);
    if (!candidates.length) return {};

    const subscriptionScopes = Array.from(
      new Set(
        candidates.map(
          candidate => `/subscriptions/${candidate.subscriptionId}`,
        ),
      ),
    );
    const bySubscription = await armTagsApi.getTagWriteAccess(
      subscriptionScopes,
    );

    const needsOwnCheck = candidates
      .filter(
        candidate =>
          bySubscription[`/subscriptions/${candidate.subscriptionId}`] !==
          'allowed',
      )
      .slice(0, MAX_RESOURCE_GROUP_CHECKS);

    const byResourceGroup = needsOwnCheck.length
      ? await armTagsApi.getTagWriteAccess(
          needsOwnCheck.map(candidate => candidate.id),
        )
      : {};

    return Object.fromEntries(
      candidates.map(candidate => {
        const subscriptionAccess =
          bySubscription[`/subscriptions/${candidate.subscriptionId}`];
        return [
          candidate.id,
          byResourceGroup[candidate.id] ??
            (subscriptionAccess === 'allowed' ? 'allowed' : 'unknown'),
        ];
      }),
    ) as TagWriteAccessByCandidate;
  }, [armTagsApi, rows]);

  return { access: value ?? {} };
};
